import React from "react";
import Section from "./Section";

export default function HTML_HEX() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🎨 HTML HEX Colors</h1>

      {/* 🔹 Highlighted Intro Box */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "1rem",
          marginBottom: "2rem",
        }}
      >
        <p className="tutorial-intro">
          HEX colors represent colors in <strong>hexadecimal format</strong>,
          starting with a <code>#</code> symbol. They are one of the most common
          ways to define colors in HTML and CSS.
        </p>
      </div>

      {/* 🔹 Explanation */}
      <p>
        A HEX color is a <strong>6-digit code</strong> that represents the
        amount of <b>Red</b>, <b>Green</b>, and <b>Blue</b> in a color. For
        example, <code>#FF0000</code> means pure red.
      </p>

      {/* Section 1️⃣ — Basic HEX Colors */}
      <Section
        title="1️⃣ Basic HEX Examples"
        html={`<div style="background-color: #FF0000; color: white; padding: 10px;">#FF0000 — Red</div>
<div style="background-color: #00FF00; color: black; padding: 10px;">#00FF00 — Green</div>
<div style="background-color: #0000FF; color: white; padding: 10px;">#0000FF — Blue</div>`}
        description="HEX codes define RGB values using hexadecimal numbers from 00 to FF (0–255 in decimal)."
      />

      {/* Section 2️⃣ — Mixing HEX Colors */}
      <Section
        title="2️⃣ Mixed HEX Colors"
        html={`<div style="background-color: #FFFF00; padding: 10px;">#FFFF00 — Yellow</div>
<div style="background-color: #FF00FF; padding: 10px;">#FF00FF — Magenta</div>
<div style="background-color: #00FFFF; padding: 10px;">#00FFFF — Cyan</div>
<div style="background-color: #808080; padding: 10px;">#808080 — Gray</div>`}
        description="You can mix red, green, and blue intensities by changing the pairs of HEX values."
      />

      {/* Section 3️⃣ — Short HEX Codes */}
      <Section
        title="3️⃣ Short HEX (3-digit) Form"
        html={`<div style="background-color: #F00; color: white; padding: 10px;">#F00 — Red</div>
<div style="background-color: #0F0; color: black; padding: 10px;">#0F0 — Green</div>
<div style="background-color: #00F; color: white; padding: 10px;">#00F — Blue</div>`}
        description="3-digit HEX shorthand is used when both digits of each color pair are the same. For example, #FF0000 → #F00."
      />

      {/* Section 4️⃣ — HEX with Transparency */}
      <Section
        title="4️⃣ HEX with Transparency (8-digit HEX)"
        html={`<div style="background-color: #FF000080; color: white; padding: 10px;">#FF000080 — Semi-transparent Red</div>
<div style="background-color: #00FF0080; padding: 10px;">#00FF0080 — Semi-transparent Green</div>
<div style="background-color: #0000FF80; color: white; padding: 10px;">#0000FF80 — Semi-transparent Blue</div>`}
        description="You can use an extra two digits in HEX for transparency (the alpha channel). Example: #RRGGBBAA."
      />

      {/* Section 5️⃣ — HEX Color Gradients */}
      <Section
        title="5️⃣ HEX Gradient Example"
        html={`<div style="background: linear-gradient(to right, #FF0000, #0000FF); height: 80px; border-radius: 10px;"></div>`}
        description="You can use HEX colors inside gradients to smoothly blend between colors."
      />

      {/* ✅ Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
          marginBottom: "2rem",
        }}
      >
        <h2>📘 Summary — HEX Color Essentials</h2>
        <ul className="tutorial-list-points">
          <li>
            HEX color format: <code>#RRGGBB</code>
          </li>
          <li>Each pair (RR, GG, BB) ranges from 00 to FF (0–255).</li>
          <li>
            Shorthand 3-digit HEX like <code>#F00</code> → <code>#FF0000</code>.
          </li>
          <li>
            8-digit HEX includes transparency: <code>#RRGGBBAA</code>.
          </li>
          <li>HEX is commonly used in web design with CSS and HTML.</li>
        </ul>
      </div>
    </div>
  );
}
